/**
 * CouponRedeemModal — enter case-sensitive promo code, redeem for coins, success animation.
 * Surfaces and typography follow active theme (card / elevated / gray scale); primary CTA uses theme gradient.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  Animated,
  Platform,
  ActivityIndicator,
  Keyboard,
} from 'react-native';
import { BaseModal } from '../../../../components/ui/BaseModal/BaseModal';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { CheckCircle, X } from 'lucide-react-native';
import { getAssetUrl, ASSET_PATHS, IDLE_COIN_SOURCE } from '../../../../constants/assets';
import { useTheme } from '../../../theme/useTheme';
import { getCouponRedeemModalStylePack } from './CouponRedeemModal.styles';
import {
  PROMO_COUPON_CODE_MAX_LENGTH,
  PROMO_COUPON_CODE_FORMAT_HINT,
  COUPON_REDEEM_COPY,
  couponMaxLengthError,
} from '../../constants/promoCoupon.constants';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../shared/helpers/imageWebAlt.helper';

const COUPON_HERO_URI = getAssetUrl(ASSET_PATHS.COUPON_REDEEM_HERO);

export interface CouponRedeemModalProps {
  isVisible: boolean;
  onDismiss: () => void;
  onRedeem: (code: string) => Promise<{ success: boolean; awarded?: number; error?: string }>;
  testID?: string;
  /** Storybook / tests: skip motion. */
  disableAnimations?: boolean;
}

export const CouponRedeemModal: React.FC<CouponRedeemModalProps> = ({
  isVisible,
  onDismiss,
  onRedeem,
  testID,
  disableAnimations = false,
}) => {
  const theme = useTheme();
  const { styles, closeIconColor, placeholderTextColor } = useMemo(
    () => getCouponRedeemModalStylePack(theme),
    [theme],
  );
  const [code, setCode] = useState('');
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successAwarded, setSuccessAwarded] = useState<number | null>(null);

  const coinScale = useRef(new Animated.Value(0)).current;
  const checkOpacity = useRef(new Animated.Value(0)).current;
  const nd = Platform.OS !== 'web';

  useEffect(() => {
    if (!isVisible) {
      setCode('');
      setInlineError(null);
      setIsSubmitting(false);
      setSuccessAwarded(null);
      coinScale.setValue(0);
      checkOpacity.setValue(0);
    }
  }, [isVisible, coinScale, checkOpacity]);

  const runSuccessAnimation = useCallback(() => {
    if (disableAnimations) {
      coinScale.setValue(1);
      checkOpacity.setValue(1);
      return;
    }
    coinScale.setValue(0);
    checkOpacity.setValue(0);
    Animated.sequence([
      Animated.parallel([
        Animated.spring(coinScale, { toValue: 1, friction: 6, tension: 120, useNativeDriver: nd }),
        Animated.timing(checkOpacity, { toValue: 1, duration: 280, useNativeDriver: nd }),
      ]),
    ]).start();
  }, [checkOpacity, coinScale, disableAnimations, nd]);

  const handleRedeem = useCallback(async () => {
    setInlineError(null);
    const trimmed = code;
    if (!trimmed) {
      setInlineError(COUPON_REDEEM_COPY.ERROR_EMPTY_CODE);
      return;
    }
    if (trimmed.length > PROMO_COUPON_CODE_MAX_LENGTH) {
      setInlineError(couponMaxLengthError(PROMO_COUPON_CODE_MAX_LENGTH));
      return;
    }
    Keyboard.dismiss();
    setIsSubmitting(true);
    try {
      const result = await onRedeem(trimmed);
      if (!result.success) {
        setInlineError(result.error ?? COUPON_REDEEM_COPY.ERROR_GENERIC);
        return;
      }
      const awarded = result.awarded ?? 0;
      setSuccessAwarded(awarded);
      runSuccessAnimation();
    } finally {
      setIsSubmitting(false);
    }
  }, [code, onRedeem, runSuccessAnimation]);

  const handleDismiss = useCallback(() => {
    Keyboard.dismiss();
    onDismiss();
  }, [onDismiss]);

  return (
    <BaseModal
      isVisible={isVisible}
      onDismiss={handleDismiss}
      testID={testID ?? 'coupon-redeem-modal'}
      contentTestID={testID ? `${testID}-content` : 'coupon-redeem-modal-content'}
      animationType={disableAnimations ? 'none' : 'fade'}
      contentContainerStyle={styles.contentWrap}
    >
      <View style={styles.modalContainer}>
        <Pressable
          style={styles.closeButton}
          onPress={handleDismiss}
          accessibilityRole="button"
          accessibilityLabel={COUPON_REDEEM_COPY.A11Y_CLOSE}
        >
          <X size={22} color={closeIconColor} />
        </Pressable>

        {successAwarded == null ? (
          <Image
            source={{ uri: COUPON_HERO_URI }}
            style={styles.heroImage}
            contentFit="contain"
            accessibilityIgnoresInvertColors
            accessibilityLabel={COUPON_REDEEM_COPY.HERO_ALT}
            alt={Platform.OS === 'web' ? COUPON_REDEEM_COPY.HERO_ALT : undefined}
          />
        ) : null}

        <View style={styles.header}>
          <Text style={styles.title}>{COUPON_REDEEM_COPY.TITLE}</Text>
          {successAwarded == null ? (
            <Text style={styles.subtitle}>{COUPON_REDEEM_COPY.ENTRY_SUBTITLE}</Text>
          ) : (
            <Text style={styles.subtitle}>{COUPON_REDEEM_COPY.SUCCESS_SUBTITLE}</Text>
          )}
        </View>

        {successAwarded != null ? (
          <View style={styles.successBlock}>
            <Animated.View style={[styles.successCoinRow, { transform: [{ scale: coinScale }] }]}>
              <Image
                source={IDLE_COIN_SOURCE}
                style={styles.successCoinImage}
                alt={WEB_IMAGE_ALT_DECORATIVE}
              />
              <Text style={styles.successAmount}>+{successAwarded.toLocaleString()}</Text>
            </Animated.View>
            <Animated.View style={[styles.successCheck, { opacity: checkOpacity }]}>
              <CheckCircle size={40} color={theme.success} />
            </Animated.View>
            <Text style={styles.successLabel}>{COUPON_REDEEM_COPY.SUCCESS_LABEL}</Text>
            <Pressable
              style={styles.doneButton}
              onPress={handleDismiss}
              accessibilityRole="button"
              accessibilityLabel={COUPON_REDEEM_COPY.A11Y_DONE}
            >
              <Text style={styles.doneButtonText}>{COUPON_REDEEM_COPY.DONE_BUTTON}</Text>
            </Pressable>
          </View>
        ) : (
          <>
            <TextInput
              style={styles.input}
              value={code}
              onChangeText={setCode}
              placeholder={COUPON_REDEEM_COPY.INPUT_PLACEHOLDER}
              placeholderTextColor={placeholderTextColor}
              autoCapitalize="none"
              autoCorrect={false}
              editable={!isSubmitting}
              maxLength={PROMO_COUPON_CODE_MAX_LENGTH}
              accessibilityLabel={COUPON_REDEEM_COPY.INPUT_A11Y_LABEL}
              testID={testID ? `${testID}-input` : 'coupon-redeem-input'}
            />
            <Text style={styles.hint}>
              {PROMO_COUPON_CODE_FORMAT_HINT} Case-sensitive · up to {PROMO_COUPON_CODE_MAX_LENGTH}{' '}
              characters.
            </Text>
            {inlineError ? <Text style={styles.errorText}>{inlineError}</Text> : null}
            <Pressable
              style={({ pressed }) => [
                styles.redeemButtonOuter,
                (isSubmitting || !code) && styles.redeemButtonDisabled,
                pressed && styles.redeemButtonPressed,
              ]}
              onPress={handleRedeem}
              disabled={isSubmitting || !code}
              accessibilityRole="button"
              accessibilityLabel={COUPON_REDEEM_COPY.A11Y_REDEEM}
              testID={testID ? `${testID}-submit` : 'coupon-redeem-submit'}
            >
              <LinearGradient
                colors={[theme.primary, theme.primaryDark]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.redeemButtonGradient}
              >
                {isSubmitting ? (
                  <ActivityIndicator color={theme.white} />
                ) : (
                  <Text style={styles.redeemButtonText}>{COUPON_REDEEM_COPY.REDEEM_BUTTON}</Text>
                )}
              </LinearGradient>
            </Pressable>
          </>
        )}
      </View>
    </BaseModal>
  );
};
